import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, BarChart, Area, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, LineChart, AreaChart, Line, ResponsiveContainer } from 'recharts';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import { getAbsenceKPIData } from '../../services/absenceAPIs';
import { useOffices } from '../../offices';
import { getExpectedWorkHours, getExpectedWorkedDays } from './helper';
import AbsenceDashboard from '../dash/AbsenceDash';

const AbsenceKPIDashboard = () => {
  const navigate = useNavigate();
  const [openAbsenceDashboard, setOpenAbsenceDashboard] = useState(false);
  const { offices, loading: officesLoading, error: officesError } = useOffices();
  const [loading, setLoading] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [selectedOffice, setSelectedOffice] = useState('all');
  const [year] = useState(new Date().getFullYear());
  const [month] = useState(new Date().getMonth() + 1);
  const [hoveredCard, setHoveredCard] = useState(null);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  // State for all KPI data
  const [kpiData, setKpiData] = useState({
    totalAbsences: 0,
    totalHours: 0,
    totalDays: 0,
    absenteeismRate: 0,
    absencesByType: [],
    absencesByLeaveType: [],
    departmentStats: [],
    topAbsentees: [],
    latenessStats: {
      totalInstances: 0,
      totalMinutes: 0,
      averageMinutes: 0,
      mostCommonHour: '8:30 AM'
    },
    weeklyTrend: [],
    monthlyTrend: []
  });

  // API calls
  const fetchKPIData = async () => {
    setLoading(true);
    try {
      const targetMonth = selectedPeriod === 'month' ? month : null;
      const data = await getAbsenceKPIData(year, targetMonth, selectedOffice);

      const absencesByType = (data.absencesByType || [])
        .filter(item => ['SICKNESS', 'LATENESS', 'PERSONAL', 'UNKNOWN'].includes(item.type))
        .map((item, idx) => ({
          ...item,
          color: ['#ff6b6b', '#feca57', '#48dbfb', '#ff9ff3', '#54a0ff'][idx % 5],
        }));

      const totalLeaveHours = absencesByType.reduce((sum, t) => sum + (t.hours || 0), 0);
      const absencesByLeaveType = (data.absencesByLeaveType || []).map(t => ({
        ...t,
        percentage: totalLeaveHours > 0 ? ((t.hours / totalLeaveHours) * 100).toFixed(1) : 0,
      }));

      const departmentStats = (data.departmentStats || []).map(dept => ({
        ...dept,
        rate: typeof dept.rate === 'number' ? parseFloat(dept.rate.toFixed(1)) : 0,
        avgHoursPerAbsence: typeof dept.avgHoursPerAbsence === 'number'
          ? parseFloat(dept.avgHoursPerAbsence.toFixed(1))
          : 0,
      }));

      const topAbsentees = (data.topAbsentees || []).map((emp, index) => ({
        id: emp.id || `${emp.firstName}-${emp.lastName}-${index}`,
        name: emp.name || `${emp.firstName} ${emp.lastName}`.trim(),
        department: emp.department || 'Unknown',
        totalDays: emp.totalDays || 0,
        totalHours: emp.totalHours != null ? parseFloat(emp.totalHours.toFixed(1)) : 0,
        frequency: emp.frequency || 0,
        lastAbsence: emp.lastAbsence || 'N/A',
      }));

      const latenessStats = {
        totalInstances: data.latenessStats?.totalInstances || 0,
        totalMinutes: data.latenessStats?.totalMinutes || 0,
        averageMinutes: data.latenessStats?.averageMinutes || 0,
        mostCommonHour: data.latenessStats?.mostCommonHour || 'N/A',
        departmentWithMost: data.latenessStats?.departmentWithMostLateness || 'N/A',
      };

      const totalAbsences = (data.weeklyTrend || []).reduce((sum, week) => sum + (week.absences || 0), 0);

      const weeklyTrend = (data.weeklyTrend || []).map((week, i) => ({
        week: week.week || `W${i + 1}`,
        absences: week.absences || 0,
        hours: week.hours != null ? parseFloat(week.hours.toFixed(1)) : 0,
        days: week.days || 0,
        percentage: totalAbsences > 0
          ? parseFloat(((week.absences || 0) / totalAbsences * 100).toFixed(1))
          : 0
      }));

      const yearlyTotalAbsences = (data.monthlyTrend || []).reduce(
        (sum, month) => sum + (month.absences || 0),
        0
      );

      const monthlyTrend = (data.monthlyTrend || []).map((month, i) => {
        const monthNames = [
          "Jan", "Feb", "Mar", "Apr", "May", "Jun",
          "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
        ];

        return {
          month: month.month
            ? month.month.charAt(0) + month.month.slice(1).toLowerCase()
            : monthNames[i],
          absences: month.absences || 0,
          hours: month.hours != null ? parseFloat(month.hours.toFixed(1)) : 0,
          days: month.days || 0,
          percentage: yearlyTotalAbsences > 0
            ? parseFloat(((month.absences || 0) / yearlyTotalAbsences * 100).toFixed(1))
            : 0,
        };
      });

      const expectedWorkHours = getExpectedWorkHours(selectedPeriod, year, selectedPeriod === 'month' ? month : null);
      const hoursLostPercentage = expectedWorkHours > 0
        ? ((data.totalHours || 0) / expectedWorkHours) * 100
        : 0;

      const expectedWorkDays = getExpectedWorkedDays(selectedPeriod, year, selectedPeriod === 'month' ? month : null);
      const daysLostPercentage = expectedWorkDays > 0
        ? ((data.totalDays || 0) / expectedWorkDays) * 100
        : 0;

      const latenessPercentage = expectedWorkDays > 0
        ? ((data.latenessStats?.totalInstances || 0) / expectedWorkDays) * 100
        : 0;

      setKpiData({
        hoursLostPercentage: parseFloat(hoursLostPercentage.toFixed(1)),
        daysLostPercentage: parseFloat(daysLostPercentage.toFixed(1)),
        latenessPercentage: parseFloat(latenessPercentage.toFixed(1)),
        totalAbsences: data.totalAbsences || 0,
        totalHours: data.totalHours != null ? parseFloat(data.totalHours.toFixed(1)) : 0,
        totalDays: data.totalDays || 0,
        absenteeismRate: data.absenteeismRate != null ? parseFloat(data.absenteeismRate.toFixed(1)) : 0,
        absencesByType,
        absencesByLeaveType,
        departmentStats,
        topAbsentees,
        latenessStats,
        weeklyTrend,
        monthlyTrend,
      });
    } catch (error) {
      console.error('Error fetching KPI data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchKPIData();
  }, [selectedPeriod, selectedOffice, year, month]);

  const StatCard = ({ title, value, subtitle, icon, trend, color = '#1976d2', onClick, index }) => (
    <div
      style={{
        background: isDark ? `linear-gradient(135deg, ${color}25 0%, ${color}10 100%)` : `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`,
        borderRadius: '20px',
        padding: '28px',
        boxShadow: hoveredCard === index
          ? `0 20px 40px ${color}30, 0 0 0 1px ${color}20`
          : (isDark ? '0 4px 20px rgba(0,0,0,0.5)' : '0 4px 20px rgba(0,0,0,0.08)'),
        position: 'relative',
        overflow: 'hidden',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
        transform: hoveredCard === index ? 'translateY(-8px) scale(1.02)' : 'translateY(0) scale(1)',
        border: `2px solid ${hoveredCard === index ? color + '40' : (isDark ? theme.palette.divider : 'transparent')}`,
      }}
      onClick={onClick}
      onMouseEnter={() => setHoveredCard(index)}
      onMouseLeave={() => setHoveredCard(null)}
    >
      {/* Animated gradient background */}
      <div style={{
        position: 'absolute',
        top: '-50%',
        right: '-50%',
        width: '200%',
        height: '200%',
        background: `radial-gradient(circle, ${color}15 0%, transparent 70%)`,
        opacity: hoveredCard === index ? 1 : 0,
        transition: 'opacity 0.4s ease',
        pointerEvents: 'none'
      }} />

      {/* Top accent line */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '3px',
        background: `linear-gradient(90deg, ${color}, ${color}80)`,
        borderRadius: '20px 20px 0 0'
      }} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'relative', zIndex: 1 }}>
        <div style={{ flex: 1 }}>
          <div style={{
            fontSize: '13px',
            color: isDark ? '#94a3b8' : '#64748b',
            marginBottom: '12px',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.5px'
          }}>
            {title}
          </div>
          <div style={{
            fontSize: '3rem',
            fontWeight: '700',
            color: color,
            lineHeight: '1',
            marginBottom: '8px',
            background: isDark ? `linear-gradient(135deg, ${color} 0%, ${color}ee 100%)` : `linear-gradient(135deg, ${color} 0%, ${color}cc 100%)`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text'
          }}>
            {value}
          </div>
          <div style={{
            fontSize: '13px',
            color: isDark ? '#94a3b8' : '#64748b',
            fontWeight: '500'
          }}>
            {subtitle}
          </div>
          {trend && (
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              marginTop: '12px',
              fontSize: '12px',
              padding: '6px 12px',
              borderRadius: '20px',
              background: trend === 'up' ? (isDark ? '#7f1d1d' : '#fef2f2') : (isDark ? '#14532d' : '#f0fdf4'),
              color: trend === 'up' ? (isDark ? '#fca5a5' : '#dc2626') : (isDark ? '#86efac' : '#16a34a'),
              fontWeight: '600'
            }}>
              <span style={{ fontSize: '16px' }}>
                {trend === 'up' ? '↗' : '↘'}
              </span>
              vs last {selectedPeriod}
            </div>
          )}
        </div>
        <div style={{
          fontSize: '56px',
          opacity: 0.15,
          transition: 'all 0.4s ease',
          transform: hoveredCard === index ? 'scale(1.2) rotate(10deg)' : 'scale(1) rotate(0deg)'
        }}>
          {icon}
        </div>
      </div>
    </div>
  );

  const PieChartComponent = ({ data, title }) => {
    const COLORS = ['#ff6b6b', '#feca57', '#48dbfb', '#ff9ff3', '#54a0ff'];
    const filteredData = data.filter(item =>
      ['SICKNESS', 'LATENESS', 'PERSONAL', 'UNKNOWN'].includes(item.type || item.leaveType)
    );

    return (
      <div style={{
        background: isDark ? '#0f172a' : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        borderRadius: '24px',
        padding: '32px',
        boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.5)' : '0 8px 32px rgba(0,0,0,0.08)',
        border: `1px solid ${isDark ? theme.palette.divider : 'rgba(226, 232, 240, 0.8)'}`,
        transition: 'all 0.3s ease'
      }}>
        <h3 style={{
          marginBottom: '28px',
          color: theme.palette.text.primary,
          fontSize: '1.4rem',
          fontWeight: '700',
        }}>
          {title}
        </h3>
        <div style={{ display: 'flex', alignItems: 'center', gap: '40px', flexWrap: 'wrap' }}>
          <div style={{ height: '280px', minWidth: '280px', position: 'relative' }}>
            {/* Center label */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              pointerEvents: 'none',
              zIndex: 10
            }}>
              <div style={{ fontSize: '2.5rem', fontWeight: '700', color: theme.palette.text.primary }}>
                {filteredData.reduce((sum, item) => sum + item.count, 0)}
              </div>
              <div style={{ fontSize: '12px', color: theme.palette.text.secondary, fontWeight: '600', marginTop: '4px' }}>
                TOTAL
              </div>
            </div>
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <defs>
                  {filteredData.map((entry, index) => (
                    <linearGradient key={`gradient-${index}`} id={`gradient-${index}`} x1="0" y1="0" x2="1" y2="1">
                      <stop offset="0%" stopColor={entry.color} stopOpacity={1} />
                      <stop offset="100%" stopColor={entry.color} stopOpacity={0.7} />
                    </linearGradient>
                  ))}
                </defs>
                <Pie
                  data={filteredData}
                  cx="50%"
                  cy="50%"
                  innerRadius={85}
                  outerRadius={125}
                  paddingAngle={3}
                  dataKey="count"
                  strokeWidth={0}
                >
                  {filteredData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={`url(#gradient-${index})`}
                      style={{
                        filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.1))',
                        transition: 'all 0.3s ease'
                      }}
                    />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(value, name, props) => [
                    `${value} instances`,
                    props.payload.type
                  ]}
                  contentStyle={{
                    background: isDark ? '#0f172a' : 'rgba(255, 255, 255, 0.95)',
                    border: 'none',
                    borderRadius: '12px',
                    boxShadow: isDark ? '0 8px 24px rgba(0,0,0,0.5)' : '0 8px 24px rgba(0,0,0,0.12)',
                    padding: '12px 16px',
                    color: theme.palette.text.primary
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div style={{ flex: 1, minWidth: '240px' }}>
            {filteredData.map((item, index) => (
              <div key={index} style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
                marginBottom: '20px',
                padding: '12px',
                borderRadius: '12px',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
                onMouseEnter={(e) => e.currentTarget.style.background = isDark ? theme.palette.action.hover : '#f8fafc'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <div style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '6px',
                  background: `linear-gradient(135deg, ${item.color}, ${item.color}cc)`,
                  boxShadow: `0 4px 12px ${item.color}40`,
                  flexShrink: 0
                }} />
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontWeight: '600',
                    marginBottom: '4px',
                    fontSize: '15px',
                    color: theme.palette.text.primary
                  }}>
                    {item.type || item.leaveType}
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: theme.palette.text.secondary,
                    fontWeight: '500'
                  }}>
                    {item.count} instances • {item.hours ? `${item.hours}h` : `${item.minutes}min`}
                  </div>
                </div>
                <div style={{
                  fontSize: '18px',
                  fontWeight: '700',
                  color: item.color,
                  minWidth: '50px',
                  textAlign: 'right'
                }}>
                  {item.percentage}%
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div style={{
      padding: '0px',
      width: '100%',
      margin: '0',
      minHeight: '100vh',
      // background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        // marginBottom: '40px', 
        flexWrap: 'wrap',
        gap: '20px',
        padding: '0 0 32px 0',
        // background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)',
        // borderRadius: '24px',
        // boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
      }}>
        <h1></h1>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            style={{
              padding: '12px 20px',
              border: 'none',
              borderRadius: '12px',
              background: isDark ? '#0f172a' : 'rgba(255,255,255,0.95)',
              fontSize: '14px',
              fontWeight: '600',
              color: theme.palette.text.primary,
              cursor: 'pointer',
              boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.5)' : '0 4px 12px rgba(0,0,0,0.1)',
              transition: 'all 0.3s ease',
              outline: 'none'
            }}
          >
            <option value="month">This Month</option>
            <option value="year">This Year</option>
          </select>
          <select
            value={selectedOffice}
            onChange={(e) => setSelectedOffice(e.target.value)}
            style={{
              padding: '12px 20px',
              border: 'none',
              borderRadius: '12px',
              background: isDark ? '#0f172a' : 'rgba(255,255,255,0.95)',
              fontSize: '14px',
              fontWeight: '600',
              color: theme.palette.text.primary,
              cursor: 'pointer',
              boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.5)' : '0 4px 12px rgba(0,0,0,0.1)',
              transition: 'all 0.3s ease',
              outline: 'none'
            }}
            disabled={officesLoading || officesError}
          >
            <option value="all">All Offices</option>
            {officesLoading ? (
              <option value="" disabled>Loading offices...</option>
            ) : officesError ? (
              <option value="" disabled>Error loading offices</option>
            ) : (
              offices.map(office => (
                <option key={office.id} value={office.name}>
                  {office.name}
                </option>
              ))
            )}
          </select>
        </div>
      </div>

      {/* KPI Cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
        gap: '24px',
        marginBottom: '40px',
        padding: '0 8px'
      }}>
        <StatCard
          title="Total Absences"
          value={kpiData.totalAbsences}
          subtitle={`This ${selectedPeriod}`}
          icon="📊"
          color="#ff6b6b"
          index={0}
        />
        <StatCard
          title="Hours Lost"
          value={`${kpiData.hoursLostPercentage}%`}
          subtitle="Productivity impact"
          icon="⏰"
          color="#feca57"
          index={1}
        />
        <StatCard
          title="Days Lost"
          value={kpiData.daysLostPercentage ? `${kpiData.daysLostPercentage}%` : '0%'}
          subtitle={`${kpiData.totalDays} Business days`}
          icon="📅"
          color="#48dbfb"
          index={2}
        />
        <StatCard
          title="Absenteeism Rate"
          value={`${kpiData.absenteeismRate}%`}
          subtitle="Of total work time"
          icon="📈"
          color="#ff9ff3"
          onClick={() => setOpenAbsenceDashboard(true)}
          index={3}
        />
        <StatCard
          title="Lateness Frequency"
          value={kpiData.latenessPercentage ? `${kpiData.latenessPercentage}%` : '0%'}
          subtitle={`${kpiData.latenessStats.totalInstances} records`}
          icon="🕐"
          color="#54a0ff"
          index={4}
        />
      </div>

      {/* Charts Row */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(450px, 1fr))',
        gap: '32px',
        marginBottom: '40px'
      }}>
        <PieChartComponent
          data={kpiData.absencesByType}
          title="Absence Distribution by Type"
        />

        <div style={{
          background: isDark ? '#0f172a' : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          borderRadius: '24px',
          padding: '32px',
          boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.5)' : '0 8px 32px rgba(0,0,0,0.08)',
          border: `1px solid ${isDark ? theme.palette.divider : 'rgba(226, 232, 240, 0.8)'}`
        }}>
          <h3 style={{
            marginBottom: '28px',
            color: theme.palette.text.primary,
            fontSize: '1.4rem',
            fontWeight: '700',
          }}>
            Department Performance
          </h3>
          <div style={{ height: '340px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={kpiData.departmentStats}>
                <defs>
                  <linearGradient id="colorHours" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8884d8" stopOpacity={0.9} />
                    <stop offset="95%" stopColor="#8884d8" stopOpacity={0.6} />
                  </linearGradient>
                  <linearGradient id="colorShare" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f08e81ff" stopOpacity={0.9} />
                    <stop offset="95%" stopColor="#f08e81ff" stopOpacity={0.6} />
                  </linearGradient>
                  <linearGradient id="colorAbsences" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.9} />
                    <stop offset="95%" stopColor="#82ca9d" stopOpacity={0.6} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={isDark ? theme.palette.divider : "#e2e8f0"} />
                <XAxis dataKey="department" tick={{ fill: isDark ? theme.palette.text.secondary : '#64748b', fontSize: 12, fontWeight: 600 }} />
                <YAxis yAxisId="hours" orientation="left" tick={{ fill: isDark ? theme.palette.text.secondary : '#64748b', fontSize: 12 }} />
                <YAxis yAxisId="rate" orientation="right" tick={{ fill: isDark ? theme.palette.text.secondary : '#64748b', fontSize: 12 }} />
                <Tooltip
                  contentStyle={{
                    background: isDark ? '#0f172a' : 'rgba(255, 255, 255, 0.95)',
                    border: 'none',
                    borderRadius: '12px',
                    boxShadow: isDark ? '0 8px 24px rgba(0,0,0,0.5)' : '0 8px 24px rgba(0,0,0,0.12)',
                    padding: '12px 16px',
                    color: theme.palette.text.primary
                  }}
                />
                <Legend wrapperStyle={{ paddingTop: '20px' }} />
                <Bar yAxisId="hours" dataKey="totalHours" fill="url(#colorHours)" name="Total Hours" radius={[8, 8, 0, 0]} />
                <Bar yAxisId="shareOfAllAbsences" dataKey="shareOfAllAbsences" fill="url(#colorShare)" name="Dept share %" radius={[8, 8, 0, 0]} />
                <Bar yAxisId="hours" dataKey="absences" fill="url(#colorAbsences)" name="Absences" radius={[8, 8, 0, 0]} />
                <Line yAxisId="rate" type="monotone" dataKey="rate" stroke="#ffc658" strokeWidth={3} name="Rate %" dot={{ r: 5, fill: '#ffc658' }} />
                <Line yAxisId="absencePercentage" type="monotone" dataKey="absencePercentage" stroke="#ff5858ff" strokeWidth={3} name="Absence %" dot={{ r: 5, fill: '#ff5858ff' }} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Trend Analysis */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '2fr 1fr',
        gap: '32px',
        marginBottom: '40px',
      }}>
        {selectedPeriod === 'month' && (
          <div style={{
            background: isDark ? '#0f172a' : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            borderRadius: '24px',
            padding: '32px',
            boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.5)' : '0 8px 32px rgba(0,0,0,0.08)',
            border: `1px solid ${isDark ? theme.palette.divider : 'rgba(226, 232, 240, 0.8)'}`,
          }}>
            <h3 style={{
              marginBottom: '28px',
              color: theme.palette.text.primary,
              fontSize: '1.4rem',
              fontWeight: '700',
            }}>
              Weekly Absence Trend
            </h3>
            <div style={{ height: '340px', marginTop: '100px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={kpiData.weeklyTrend}>
                  <defs>
                    <linearGradient id="colorPercentage" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#51bae7ff" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#51bae7ff" stopOpacity={0.1} />
                    </linearGradient>
                    <linearGradient id="colorBarWeekly" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8884d8" stopOpacity={0.9} />
                      <stop offset="95%" stopColor="#8884d8" stopOpacity={0.6} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? theme.palette.divider : "#e2e8f0"} />
                  <XAxis dataKey="week" tick={{ fill: isDark ? theme.palette.text.secondary : '#64748b', fontSize: 12, fontWeight: 600 }} />
                  <YAxis yAxisId="left" tick={{ fill: isDark ? theme.palette.text.secondary : '#64748b', fontSize: 12 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fill: isDark ? theme.palette.text.secondary : '#64748b', fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      background: isDark ? '#0f172a' : 'rgba(255, 255, 255, 0.95)',
                      border: 'none',
                      borderRadius: '12px',
                      boxShadow: isDark ? '0 8px 24px rgba(0,0,0,0.5)' : '0 8px 24px rgba(0,0,0,0.12)',
                      padding: '12px 16px',
                      color: theme.palette.text.primary
                    }}
                  />
                  <Legend wrapperStyle={{ paddingTop: '20px' }} />
                  <Area yAxisId="left" type="monotone" dataKey="percentage" stroke="#51bae7ff" strokeWidth={2} fill="url(#colorPercentage)" name="Absence %" />
                  <Bar yAxisId="left" dataKey="absences" fill="url(#colorBarWeekly)" name="Absences" radius={[8, 8, 0, 0]} />
                  <Line yAxisId="right" type="monotone" dataKey="hours" stroke="#82ca9d" strokeWidth={3} name="Hours" dot={{ r: 5, fill: '#82ca9d' }} />
                  <Line yAxisId="right" type="monotone" dataKey="days" stroke="#ffc658" strokeWidth={3} name="Days" dot={{ r: 5, fill: '#ffc658' }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {selectedPeriod === 'year' && (
          <div style={{
            background: isDark ? '#0f172a' : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            borderRadius: '24px',
            padding: '32px',
            boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.5)' : '0 8px 32px rgba(0,0,0,0.08)',
            border: `1px solid ${isDark ? theme.palette.divider : 'rgba(226, 232, 240, 0.8)'}`
          }}>
            <h3 style={{
              marginBottom: '28px',
              color: theme.palette.text.primary,
              fontSize: '1.4rem',
              fontWeight: '700',
            }}>
              Monthly Absence Trend
            </h3>
            <div style={{ height: '340px', marginTop: '100px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={kpiData.monthlyTrend}>
                  <defs>
                    <linearGradient id="colorPercentageMonthly" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#51bae7ff" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#51bae7ff" stopOpacity={0.1} />
                    </linearGradient>
                    <linearGradient id="colorBarMonthly" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8884d8" stopOpacity={0.9} />
                      <stop offset="95%" stopColor="#8884d8" stopOpacity={0.6} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke={isDark ? theme.palette.divider : "#e2e8f0"} />
                  <XAxis dataKey="month" tick={{ fill: isDark ? theme.palette.text.secondary : '#64748b', fontSize: 12, fontWeight: 600 }} />
                  <YAxis yAxisId="left" tick={{ fill: isDark ? theme.palette.text.secondary : '#64748b', fontSize: 12 }} />
                  <YAxis yAxisId="right" orientation="right" tick={{ fill: isDark ? theme.palette.text.secondary : '#64748b', fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      background: isDark ? '#0f172a' : 'rgba(255, 255, 255, 0.95)',
                      border: 'none',
                      borderRadius: '12px',
                      boxShadow: isDark ? '0 8px 24px rgba(0,0,0,0.5)' : '0 8px 24px rgba(0,0,0,0.12)',
                      padding: '12px 16px',
                      color: theme.palette.text.primary
                    }}
                  />
                  <Legend wrapperStyle={{ paddingTop: '20px' }} />
                  <Area yAxisId="left" type="monotone" dataKey="percentage" stroke="#51bae7ff" strokeWidth={2} fill="url(#colorPercentageMonthly)" name="Absence %" />
                  <Bar yAxisId="left" dataKey="absences" fill="url(#colorBarMonthly)" name="Absences" radius={[8, 8, 0, 0]} />
                  <Line yAxisId="right" type="monotone" dataKey="hours" stroke="#82ca9d" strokeWidth={3} name="Hours" dot={{ r: 5, fill: '#82ca9d' }} />
                  <Line yAxisId="right" type="monotone" dataKey="days" stroke="#ffc658" strokeWidth={3} name="Days" dot={{ r: 5, fill: '#ffc658' }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Lateness Insights */}
        <div style={{
          background: isDark ? '#0f172a' : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
          borderRadius: '24px',
          padding: '32px',
          boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.5)' : '0 8px 32px rgba(0,0,0,0.08)',
          border: `1px solid ${isDark ? theme.palette.divider : 'rgba(226, 232, 240, 0.8)'}`
        }}>
          <h3 style={{
            marginBottom: '32px',
            color: theme.palette.text.primary,
            fontSize: '1.4rem',
            fontWeight: '700',
          }}>
            Lateness Insights
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
            <div style={{
              textAlign: 'center',
              padding: '20px',
              background: isDark ? alpha(theme.palette.primary.main, 0.1) : 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
              borderRadius: '16px',
              border: `2px solid ${isDark ? alpha(theme.palette.primary.main, 0.3) : '#93c5fd'}`
            }}>
              <div style={{ fontSize: '12px', color: theme.palette.text.secondary, marginBottom: '8px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Total Instances
              </div>
              <div style={{
                fontSize: '2.5rem',
                fontWeight: '700',
                color: isDark ? '#93c5fd' : 'transparent',
                background: isDark ? 'none' : 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
                WebkitBackgroundClip: isDark ? 'border-box' : 'text',
                WebkitTextFillColor: isDark ? '#93c5fd' : 'transparent',
                backgroundClip: isDark ? 'border-box' : 'text'
              }}>
                {kpiData.latenessStats.totalInstances}
              </div>
            </div>

            <div style={{
              textAlign: 'center',
              padding: '20px',
              background: isDark ? alpha(theme.palette.warning.main, 0.1) : 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
              borderRadius: '16px',
              border: `2px solid ${isDark ? alpha(theme.palette.warning.main, 0.3) : '#fed7aa'}`
            }}>
              <div style={{ fontSize: '12px', color: theme.palette.text.secondary, marginBottom: '8px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Average Minutes
              </div>
              <div style={{
                fontSize: '2.5rem',
                fontWeight: '700',
                color: isDark ? '#fcd34d' : 'transparent',
                background: isDark ? 'none' : 'linear-gradient(135deg, #ff9f43 0%, #feca57 100%)',
                WebkitBackgroundClip: isDark ? 'border-box' : 'text',
                WebkitTextFillColor: isDark ? '#fcd34d' : 'transparent',
                backgroundClip: isDark ? 'border-box' : 'text'
              }}>
                {kpiData.latenessStats.averageMinutes}
              </div>
            </div>

            <div style={{
              textAlign: 'center',
              padding: '20px',
              background: isDark ? alpha(theme.palette.error.main, 0.1) : 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
              borderRadius: '16px',
              border: `2px solid ${isDark ? alpha(theme.palette.error.main, 0.3) : '#fecaca'}`
            }}>
              <div style={{ fontSize: '12px', color: theme.palette.text.secondary, marginBottom: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Most Common Time
              </div>
              <div style={{
                padding: '10px 20px',
                background: isDark ? alpha(theme.palette.error.main, 0.2) : 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                color: isDark ? '#fca5a5' : '#dc2626',
                borderRadius: '24px',
                fontSize: '16px',
                fontWeight: '700',
                display: 'inline-block',
                border: `2px solid ${isDark ? 'transparent' : '#fca5a5'}`
              }}>
                {kpiData.latenessStats.mostCommonHour}
              </div>
            </div>

            {/* <div style={{ 
              textAlign: 'center',
              padding: '20px',
              background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
              borderRadius: '16px',
              border: '2px solid #fcd34d'
            }}>
              <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                Top Department
              </div>
              <div style={{
                padding: '10px 20px',
                background: 'linear-gradient(135deg, #fde68a 0%, #fcd34d 100%)',
                color: '#d97706',
                borderRadius: '24px',
                fontSize: '16px',
                fontWeight: '700',
                display: 'inline-block',
                border: '2px solid #fbbf24'
              }}>
                {kpiData.latenessStats.departmentWithMost}
              </div> 
            </div>*/}
          </div>
        </div>
      </div>

      {/* Top Absentees Table */}
      <div style={{
        background: isDark ? '#0f172a' : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        borderRadius: '24px',
        padding: '32px',
        boxShadow: isDark ? '0 8px 32px rgba(0,0,0,0.5)' : '0 8px 32px rgba(0,0,0,0.08)',
        marginBottom: '32px',
        border: `1px solid ${isDark ? theme.palette.divider : 'rgba(226, 232, 240, 0.8)'}`
      }}>
        <h3 style={{
          marginBottom: '28px',
          color: theme.palette.text.primary,
          fontSize: '1.4rem',
          fontWeight: '700',
        }}>
          Top Absentees This {selectedPeriod === 'month' ? 'Month' : 'Year'}
        </h3>
        <div>
          <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 8px' }}>
            <thead>
              <tr style={{ background: isDark ? alpha(theme.palette.divider, 0.5) : 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)' }}>
                <th style={{
                  padding: '16px 20px',
                  textAlign: 'left',
                  color: isDark ? theme.palette.text.secondary : '#475569',
                  fontWeight: '700',
                  fontSize: '13px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  borderRadius: '12px 0 0 12px'
                }}>Employee</th>
                <th style={{
                  padding: '16px 20px',
                  textAlign: 'left',
                  color: isDark ? theme.palette.text.secondary : '#475569',
                  fontWeight: '700',
                  fontSize: '13px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>Department</th>
                <th style={{
                  padding: '16px 20px',
                  textAlign: 'center',
                  color: isDark ? theme.palette.text.secondary : '#475569',
                  fontWeight: '700',
                  fontSize: '13px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>Total Days</th>
                <th style={{
                  padding: '16px 20px',
                  textAlign: 'center',
                  color: isDark ? theme.palette.text.secondary : '#475569',
                  fontWeight: '700',
                  fontSize: '13px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px'
                }}>Total Hours</th>
                <th style={{
                  padding: '16px 20px',
                  textAlign: 'center',
                  color: isDark ? theme.palette.text.secondary : '#475569',
                  fontWeight: '700',
                  fontSize: '13px',
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                  borderRadius: '0 12px 12px 0'
                }}>Frequency</th>
              </tr>
            </thead>
            <tbody>
              {kpiData.topAbsentees.map((employee, index) => (
                <tr key={employee.id} style={{
                  background: isDark ? alpha('#0b1120', 0.4) : 'white',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateX(4px)';
                    e.currentTarget.style.boxShadow = isDark ? '0 4px 20px rgba(0,0,0,0.5)' : '0 4px 20px rgba(0,0,0,0.08)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateX(0)';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                >
                  <td style={{
                    padding: '18px 20px',
                    fontSize: '15px',
                    fontWeight: '600',
                    color: theme.palette.text.primary,
                    borderRadius: '12px 0 0 12px',
                    borderTop: `1px solid ${isDark ? theme.palette.divider : '#f1f5f9'}`,
                    borderBottom: `1px solid ${isDark ? theme.palette.divider : '#f1f5f9'}`,
                    borderLeft: `1px solid ${isDark ? theme.palette.divider : '#f1f5f9'}`
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      {employee.name}
                    </div>
                  </td>
                  <td style={{
                    padding: '18px 20px',
                    fontSize: '14px',
                    color: theme.palette.text.secondary,
                    fontWeight: '500',
                    borderTop: `1px solid ${isDark ? theme.palette.divider : '#f1f5f9'}`,
                    borderBottom: `1px solid ${isDark ? theme.palette.divider : '#f1f5f9'}`
                  }}>{employee.department}</td>
                  <td style={{
                    padding: '18px 20px',
                    textAlign: 'center',
                    fontSize: '15px',
                    fontWeight: '700',
                    color: theme.palette.text.primary,
                    borderTop: `1px solid ${isDark ? theme.palette.divider : '#f1f5f9'}`,
                    borderBottom: `1px solid ${isDark ? theme.palette.divider : '#f1f5f9'}`,
                  }}>
                    <span style={{
                      background: isDark ? alpha(theme.palette.primary.main, 0.2) : 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                      padding: '6px 14px',
                      borderRadius: '8px',
                      border: `1px solid ${isDark ? 'transparent' : '#93c5fd'}`,
                      color: isDark ? '#93c5fd' : 'inherit'
                    }}>
                      {employee.totalDays}
                    </span>
                  </td>
                  <td style={{
                    padding: '18px 20px',
                    textAlign: 'center',
                    fontSize: '15px',
                    fontWeight: '700',
                    color: theme.palette.text.primary,
                    borderTop: `1px solid ${isDark ? theme.palette.divider : '#f1f5f9'}`,
                    borderBottom: `1px solid ${isDark ? theme.palette.divider : '#f1f5f9'}`
                  }}>
                    <span style={{
                      background: isDark ? alpha(theme.palette.warning.main, 0.2) : 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
                      padding: '6px 14px',
                      borderRadius: '8px',
                      border: `1px solid ${isDark ? 'transparent' : '#fed7aa'}`,
                      color: isDark ? '#fcd34d' : 'inherit'
                    }}>
                      {employee.totalHours}h
                    </span>
                  </td>
                  <td style={{
                    padding: '18px 20px',
                    textAlign: 'center',
                    fontSize: '15px',
                    fontWeight: '700',
                    color: theme.palette.text.primary,
                    borderRadius: '0 12px 12px 0',
                    borderTop: `1px solid ${isDark ? theme.palette.divider : '#f1f5f9'}`,
                    borderBottom: `1px solid ${isDark ? theme.palette.divider : '#f1f5f9'}`,
                    borderRight: `1px solid ${isDark ? theme.palette.divider : '#f1f5f9'}`
                  }}>
                    <span style={{
                      background: isDark ? alpha(theme.palette.error.main, 0.2) : 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
                      padding: '6px 14px',
                      borderRadius: '8px',
                      border: `1px solid ${isDark ? 'transparent' : '#fecaca'}`,
                      color: isDark ? '#fca5a5' : 'inherit'
                    }}>
                      {employee.frequency}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Alert */}
      {kpiData.absenteeismRate > 15 && (
        <div style={{
          background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
          border: '2px solid #fbbf24',
          borderRadius: '16px',
          padding: '24px',
          marginBottom: '32px',
          boxShadow: '0 4px 20px rgba(251, 191, 36, 0.2)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px'
          }}>
            <div style={{
              fontSize: '32px',
              background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              width: '56px',
              height: '56px',
              borderRadius: '12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              ⚠️
            </div>
            <div>
              <div style={{ fontWeight: '700', color: '#92400e', marginBottom: '6px', fontSize: '18px' }}>
                High Absenteeism Alert
              </div>
              <div style={{ color: '#92400e', fontSize: '14px', lineHeight: '1.6' }}>
                The current absence rate of {kpiData.absenteeismRate}% exceeds the recommended threshold of 15%.
                Consider implementing wellness programs or reviewing workload distribution.
              </div>
            </div>
          </div>
        </div>
      )}

      {loading && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(255,255,255,0.95)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          backdropFilter: 'blur(8px)'
        }}>
          <div style={{
            textAlign: 'center',
            padding: '40px',
            background: 'white',
            borderRadius: '20px',
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              border: '4px solid #e2e8f0',
              borderTop: '4px solid #1976d2',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 20px'
            }} />
            <div style={{ fontSize: '18px', color: '#64748b', fontWeight: '600' }}>Loading KPI data...</div>
          </div>
        </div>
      )}

      <Dialog
        open={openAbsenceDashboard}
        onClose={() => setOpenAbsenceDashboard(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Absence Rate Details
          <IconButton
            aria-label="close"
            onClick={() => setOpenAbsenceDashboard(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <AbsenceDashboard
            sick={true}
            perso={true}
            late={true}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAbsenceDashboard(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AbsenceKPIDashboard;